/**
 * LeadManagerSettingsPage — Personal preferences for the Lead Manager.
 *
 * Apr 28, 2026 (Squad-D). Screen 11 of 12.
 *
 * - Notifications (desktop / mobile / sound)
 * - Workflow (default snooze duration, auto-redirect after action)
 * - Keyboard shortcuts reference
 *
 * Settings persist to localStorage (no backend needed for v1). Backend can
 * later mirror to /users/me/preferences on save.
 */

import { useEffect, useState } from 'react';
import { PageShell, Panel, FieldLabel, KbdHint } from './_lmShared';

const STORAGE_KEY = 'aura.lm.settings.v1';

interface Settings {
  desk: boolean;
  mob: boolean;
  sound: boolean;
  snoozeMin: number;
  autoRedirect: boolean;
}

const DEFAULT_SETTINGS: Settings = {
  desk: true,
  mob: true,
  sound: true,
  snoozeMin: 120,
  autoRedirect: true,
};

function readSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function writeSettings(s: Settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function LeadManagerSettingsPage() {
  const [vals, setVals] = useState<Settings>(DEFAULT_SETTINGS);

  useEffect(() => {
    setVals(readSettings());
  }, []);

  function patch<K extends keyof Settings>(key: K, value: Settings[K]) {
    setVals((s) => {
      const next = { ...s, [key]: value };
      writeSettings(next);
      return next;
    });
  }

  return (
    <PageShell title="Settings" sub="Lead Manager preferences">
      <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Panel title="Notifications" sub="Where and how you hear about new inbound">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Toggle
              label="Desktop browser notifications"
              sub="Permission required · Chrome on macOS / Windows"
              value={vals.desk}
              onChange={(v) => patch('desk', v)}
            />
            <Toggle
              label="Mobile push (Aura mobile companion)"
              sub="Quiet hours: 22:00 – 07:00 IST"
              value={vals.mob}
              onChange={(v) => patch('mob', v)}
            />
            <Toggle
              label="Sound for new inbound"
              sub="Plays a soft chime · only when tab is active"
              value={vals.sound}
              onChange={(v) => patch('sound', v)}
            />
          </div>
        </Panel>

        <Panel title="Workflow" sub="Defaults for the verify loop">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <FieldLabel>Default snooze duration</FieldLabel>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[30, 60, 120, 240, 480].map((m) => (
                  <button
                    key={m}
                    className="btn btn-sm"
                    onClick={() => patch('snoozeMin', m)}
                    style={{
                      background: vals.snoozeMin === m ? 'var(--ink-0)' : 'var(--ivory-1)',
                      color: vals.snoozeMin === m ? 'var(--ivory-0)' : 'var(--ink-2)',
                      border:
                        '1px solid ' + (vals.snoozeMin === m ? 'var(--ink-0)' : 'var(--line)'),
                    }}
                  >
                    {m < 60 ? `${m} min` : `${m / 60} h`}
                  </button>
                ))}
              </div>
            </div>
            <Toggle
              label="Auto-redirect to next unverified after action"
              sub="V/R/N keep working without clicks"
              value={vals.autoRedirect}
              onChange={(v) => patch('autoRedirect', v)}
            />
          </div>
        </Panel>

        <Panel title="Keyboard shortcuts">
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}
          >
            {(
              [
                ['V', 'Verify current'],
                ['R', 'Reject current'],
                ['N', 'Next in queue'],
                ['A', 'Open assign'],
                ['/', 'Search'],
                ['Esc', 'Back to queue'],
              ] as [string, string][]
            ).map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: '1px solid var(--ivory-3)',
                }}
              >
                <KbdHint k={k} />
                <span>{v}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </PageShell>
  );
}

function Toggle({
  label,
  sub,
  value,
  onChange,
}: {
  label: string;
  sub?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div>}
      </div>
      <button
        onClick={() => onChange(!value)}
        style={{
          width: 38,
          height: 22,
          borderRadius: 11,
          background: value ? 'var(--forest)' : 'var(--ivory-3)',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          transition: 'background .15s',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: value ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: 9,
            background: '#fff',
            transition: 'left .15s',
            boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          }}
        />
      </button>
    </div>
  );
}

export default LeadManagerSettingsPage;
